import { useMemo } from 'react'
import { resolveAccess } from './resolveAccess'
import { ROUTE_REGISTRY_ENTRIES } from './routeRegistry'
import { useAccessSources } from './useAccessSources'
import type { AccessAnswer, AccessSources } from './accessRules'
import type { AskedRecord } from './useRecordAuthorization'
import type { PermissionSetEntity } from '@omnea-app/authz'
import type { RouteKey } from '@omnea-app/fe-routing/routes/routeKeys'

const answersFor = (sources: AccessSources, entity: PermissionSetEntity | null): ReadonlyMap<RouteKey, AccessAnswer> => {
  const answers = new Map<RouteKey, AccessAnswer>()

  for (const [key, definition] of ROUTE_REGISTRY_ENTRIES) {
    const answer = resolveAccess(definition, sources)
    const record = definition.record
    // A record rule of another entity has no answer here, and `resolveAccess` throws rather than
    // guess one, so those routes keep the answer their address alone gives.
    const readsRecord = record != null && record.authorization.entity === entity
    answers.set(key, answer === 'allowed' && readsRecord ? resolveAccess(record, sources) : answer)
  }

  return answers
}

/**
 * What every route answers for the current caller, so enforcement and the links that point at a route
 * cannot disagree.
 */
export const useRouteAccess = (): ReadonlyMap<RouteKey, AccessAnswer> => {
  const sources = useAccessSources()

  return useMemo(() => answersFor(sources, null), [sources])
}

/**
 * The same map for navigation sitting on one record, with the record half of that entity's detail
 * routes folded in — so a tab whose route carries a verb narrower than `canView` hides rather than
 * leading to a not-found.
 *
 * Separate from {@link useRouteAccess} because `RouteAccessGate` reads that map to decide the route
 * half alone: a denied route redirects, where a denied record must not say the record exists.
 */
export const useRecordRouteAccess = (asked: AskedRecord<PermissionSetEntity>): ReadonlyMap<RouteKey, AccessAnswer> => {
  const sources = useAccessSources(asked)
  const { entity } = asked

  return useMemo(() => answersFor(sources, entity), [sources, entity])
}
